import PropTypes from 'prop-types';
import { m } from 'framer-motion';
// @mui
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Box, Grid, Link, Paper, Rating, Container, Typography } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/minimal/useResponsive';
// utils
import { bgBlur, bgGradient } from '../../../utils/minimal/cssStyles';
import { fDate } from '../../../utils/minimal/formatTime';
// components
import Iconify from '../../../components/minimal/iconify';
import { MotionViewport, varFade } from '../../../components/minimal/animate';
import { Avatar } from '@mui/material';

// ----------------------------------------------------------------------

const TESTIMONIALS = [
  {
    name: 'עמיחי לווינסקי',
    role: 'מנכ״ל בית ציוני אמריקה',
    rating: 5,
    dateCreate: new Date('Jun 4 2024'),
    content: ``,
    link: 'https://zoatlv.co.il',
    img: "/assets/images/home/ami.png",
  },
  {
    name: 'Gary Gohzlan',
    role: ' בעלים ומנהל, מועדון Hormesis',
    rating: 5,
    dateCreate: new Date('Apr 26 2024'),
    content: ``,
    link: 'https://hormesis.club',
    img: "/assets/images/home/gary.png",
  },
  {
    name: 'אורי בייליס',
    rating: 5,
    dateCreate: new Date('Jan 8 2025'),
    img: "/assets/images/home/ori.png",
    role: 'מנהל תרבות עריית רמת גן',
    content: ``,
    link: 'https://www.ramat-gan.muni.il',
  },
  {
    name: 'Mor Cahlon',
    rating: 5,
    dateCreate: new Date('Jun 4 2024'),
    content: ``,
    link: 'https://easytix.co.il',
    img: "/assets/images/home/mor.png",
  },
  {
    name: 'עידו מינקובסקי',
    role: 'בעלים ומנהל Minkovsky Media',
    rating: 5,
    dateCreate: new Date('Jun 4 2024'),
    content: ``,
    img: "/assets/images/home/eido.png",
    link: 'https://minkovsky-media.com/en',
  },
  {
    name: 'אודי אילון',
    role: 'מפיק, ויוצר תוכן',
    rating: 5,
    dateCreate: new Date('Jun 4 2024'),
    content: ``,
    img: "/assets/images/home/udi.png",
  },
];

const StyledRoot = styled('div')(({ theme }) => ({
  ...bgGradient({
    color: alpha(theme.palette.grey[900], 0.8),
    imgUrl: '/assets/images/testimonials.jpg',
  }),
  textAlign: 'center',
  padding: theme.spacing(10, 0),
  [theme.breakpoints.up('md')]: {
    padding: 0,
    height: 840,
    textAlign: 'left',
    overflow: 'hidden',
  },
}));

// ----------------------------------------------------------------------

export default function AboutTestimonials() {
  const isDesktop = useResponsive('up', 'md');

  return (
    <StyledRoot>
      <Container component={MotionViewport} sx={{ position: 'relative', height: 1 }}>
        <Grid
          container
          spacing={3}
          alignItems="center"
          justifyContent={{ xs: 'center', md: 'space-between' }}
          sx={{ height: 1 }}
        >
          <Grid item xs={10} md={4}>
            <Box sx={{ maxWidth: { md: 360 } }}>
              <m.div variants={varFade().inUp}>
                <Typography
                  component="p"
                  variant="overline"
                  sx={{ mb: 2, color: 'text.secondary' }}
                >
                  המלצות
                </Typography>
              </m.div>

              <m.div variants={varFade().inUp}>
                <Typography variant="h2" sx={{ mb: 3, color: 'common.white' }}>
                  לקוחות שאוהבים לעבוד איתנו
                </Typography>
              </m.div>

              <m.div variants={varFade().inUp}>
                <Typography sx={{ color: 'common.white' }}>
                  המטרה שלנו היא לבנות מוצר שהלקוחות אוהבים,
                  ולכן אנחנו באמת מקשיבים למה שיש לכם לומר. <br />
                  אנחנו תמיד עובדים בלי הפסקה לשפר
                  כדי לתת לכם את המוצר והשירות הכי טוב שאפשר.
                </Typography>
              </m.div>

              {!isDesktop && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                  <m.div variants={varFade().inUp}>
                    <TestimonialLink />
                  </m.div>
                </Box>
              )}
            </Box>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            lg={6}
            sx={{
              right: { md: 24 },
              position: { md: 'absolute' },
            }}
          >
            <Grid container spacing={isDesktop ? 3 : 0} alignItems="center">
              <Grid item xs={12} md={6}>
                {TESTIMONIALS.slice(0, 3).map((testimonial) => (
                  <m.div key={testimonial.name} variants={varFade().inUp}>
                    <TestimonialCard testimonial={testimonial} />
                  </m.div>
                ))}
              </Grid>

              <Grid item xs={12} md={6}>
                {TESTIMONIALS.slice(3, 6).map((testimonial) => (
                  <m.div key={testimonial.name} variants={varFade().inUp}>
                    <TestimonialCard testimonial={testimonial} />
                  </m.div>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* {isDesktop && (
          <Box sx={{ bottom: 60, position: 'absolute' }}>
            <m.div variants={varFade().inLeft}>
              <TestimonialLink />
            </m.div>
          </Box>
        )} */}
      </Container>
    </StyledRoot>
  );
}

// ----------------------------------------------------------------------

TestimonialCard.propTypes = {
  testimonial: PropTypes.shape({
    name: PropTypes.string,
    rating: PropTypes.number,
    content: PropTypes.string,
    dateCreate: PropTypes.instanceOf(Date),
    link: PropTypes.string,
    img: PropTypes.string,
  }),
};

function TestimonialCard({ testimonial }) {
  const theme = useTheme();

  const { name, rating, dateCreate, content, link, img, role } = testimonial;

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        textAlign: 'center',
        mt: 3,
        p: 3,
        color: 'common.white',
        ...bgBlur({
          color: theme.palette.common.white,
          opacity: 0.04,
        }),
      }}
    >
      <Avatar src={img} sx={{ width: 64, height: 64, mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        {name}
      </Typography>

      <Typography variant="body2" gutterBottom>
        {role}
      </Typography>

      <Typography gutterBottom component="div" variant="caption" sx={{ color: 'grey.500' }}>
        {fDate(dateCreate)}
      </Typography>

      <Rating value={rating} readOnly size="small" />

      <Typography variant="body2" sx={{ mt: 1.5 }}>
        {content}
      </Typography>
      {link && <Link variant="body2" href={link} target="_blank" sx={{ color: 'inherit', opacity: '0.7', whiteSpace: 'nowrap' }}>
        {link.replace(/(^\w+:|^)\/\//, '')}
      </Link>}
    </Paper>
  );
}

// ----------------------------------------------------------------------

function TestimonialLink(props) {
  return (
    <Link href="#" variant="subtitle2" sx={{ display: 'flex', alignItems: 'center' }}>
      {props.children}
      <Iconify icon="ic:round-arrow-right-alt" sx={{ ml: 1 }} />
    </Link>
  );
}
